import React, { useState } from 'react';
import { UserPlus } from 'lucide-react';
import toast from 'react-hot-toast';
import { assignRelistingOpportunity } from '../../../api/dashboard';
import AddSellerLeadModal from '../../leads/components/AddSellerLeadModal';
import { formatMelbourneShortDate } from '../../../utils/melbourneTime';

const money = (n) => `$${String(Math.round(Number(n))).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;

// What the agent knows about the property, carried into the lead's notes.
const homerNote = (row) => {
  const why = row.withdrawn && row.withdrawn_date
    ? `withdrawn ${formatMelbourneShortDate(row.withdrawn_date)}`
    : row.reason;
  const listedWith = [row.agent_name, row.agency_name].filter(Boolean).join(' with ');
  const guide = row.guide_label || (row.current_guide ? money(row.current_guide) : '');
  const sheetNotes = (row.owner_contacts || []).map((c) => c.notes).filter(Boolean);
  return [
    `Re-listing opportunity: ${why}.`,
    listedWith ? `Listed with ${listedWith}.` : '',
    guide ? `Last guide ${guide}.` : '',
    (row.listing_urls || [])[0] || '',
    sheetNotes.length ? `From the office's spreadsheets: ${sheetNotes.join(' / ')}` : '',
  ].filter(Boolean).join('\n');
};

// The first two people on file (Monday 13354971743), likeliest owner first, fill the owner and second owner.
const ownerFields = (contacts = []) => {
  const person = (c) => ({
    name: c.contact_name || (c.owner_name && !/^the proprietors?$/i.test(c.owner_name) ? c.owner_name : ''),
    phone: (c.phones || [])[0] || '',
    email: c.email || '',
  });
  // Someone who sold the property through us no longer owns it.
  const current = contacts.filter((c) => c.role !== 'Sold through Collings');
  const reachable = current.filter((c) => (c.phones || []).length || c.email);
  const [first, second] = reachable.length ? reachable : current;
  if (!first) return {};
  const a = person(first);
  const out = { name: a.name, phone: a.phone, email: a.email };
  if (second) {
    const b = person(second);
    if (b.phone !== a.phone || b.email !== a.email) {
      Object.assign(out, { second_owner_name: b.name, second_owner_phone: b.phone, second_owner_email: b.email });
    }
  }
  return out;
};

// The viewer's own Database owners (Monday 13354971743, 28 Oct 2026) after the
// spreadsheet's, in the same shape. A pipeline lead is already on the board, so not this.
const databaseContacts = (people = []) =>
  (people || [])
    .filter((p) => p.source === 'database')
    .map((p) => ({ contact_name: p.name, phones: p.phones || [], email: (p.emails || [])[0] || '' }));

/**
 * "Add Lead" on a re-listing opportunity (George Bottas CRM-2026-00114, 21 Oct
 * 2026, replacing Christian's "Assign"): opens the Prospects Add Lead form with
 * the address filled in so the agent can enter the owner (and a second owner).
 * The agent picks where it lands on Prospects: New, or Connected as Cold /
 * Warm / Hot (Paul Jimoulis CRM-2026-00116). The row leaves the list.
 * Used on the dashboard card and the Re-listing Opportunities page.
 * `onAssigned(row)` lets the caller drop the row straight away.
 */
const RelistingAssignButton = ({ row, onAssigned, className = '' }) => {
  const [open, setOpen] = useState(false);
  const campaignId = row.campaign_id || row.listing_id;

  const linkLead = async (leadId) => {
    try {
      await assignRelistingOpportunity(campaignId, leadId);
      if (onAssigned) onAssigned(row);
    } catch (err) {
      // The lead is saved either way; a colleague may have taken the row meanwhile.
      const msg = err?.response?.data?.error || 'Could not take this property off the list.';
      toast.error(msg);
      if (err?.response?.status === 409 || err?.response?.status === 404) {
        if (onAssigned) onAssigned(row);
      }
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setOpen(true);
        }}
        disabled={!campaignId}
        title="Add the owner as a lead on Prospects"
        className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-[12px] font-semibold text-white bg-[#55C5D0] hover:bg-[#45b5c0] disabled:opacity-60 whitespace-nowrap ${className}`}
      >
        <UserPlus className="w-3.5 h-3.5" />
        Add Lead
      </button>
      {open && (
        <div onClick={(e) => e.stopPropagation()}>
          <AddSellerLeadModal
            isOpen
            onClose={() => setOpen(false)}
            initial={{
              property_address: row.address || '',
              property_suburb: row.suburb || '',
              property_state: row.state || '',
              property_postcode: row.postcode || '',
              source: 'relisting',
              initial_notes: homerNote(row),
              ...ownerFields([...(row.owner_contacts || []), ...databaseContacts(row.crm_people)]),
            }}
            choosePlacement
            successMessage={(where) => `${row.address} added to Prospects under ${where}.`}
            onSaved={linkLead}
          />
        </div>
      )}
    </>
  );
};

export default RelistingAssignButton;
